import Grid from "antd/lib/grid";
import Typography from "antd/lib/typography";
import clsx from "clsx";
import { TFunction } from "i18next";
import { entries, groupBy } from "lodash";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ProgressionLegend, BriefStatusBadge } from "src/features/dashboards/components";

import {
    BriefAssignmentState,
    getResourceIRI,
    IBriefAssignmentListQueryResult,
    type IClassroom,
    useBriefAssignmentListQuery,
} from "@sol/sdk";
import { UserProfile01 } from "@sol/uikit/core/icons/ProfileAndUsers/UserProfile01";
import { UsersProfiles01 } from "@sol/uikit/core/icons/ProfileAndUsers/UsersProfiles01";
import { Empty } from "@sol/uikit/data-display/Empty/Empty";
import { Table, type TableProps } from "@sol/uikit/data-display/Table/Table";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";
import { EnumDictionary } from "@sol/utils";

function getColumns(classroom: IClassroom | undefined, t: TFunction, isMobile: boolean) {
    const learners = classroom?.learners ?? [];

    const columns: TableProps["columns"] = [
        {
            title: t("table.column.type"),
            dataIndex: "type",
            key: "type",
            align: "center" as const,
            width: 80,
            render: (text: string) => {
                const Icon = text === "individual" ? UserProfile01 : UsersProfiles01;
                return (
                    <Typography.Text title={text} className="line-clamp-1 overflow-hidden font-ant-strong break-words">
                        <Icon variant={"antd"} className="inline-flex size-[1.2em]" />
                    </Typography.Text>
                );
            },
        },
        {
            title: t("table.column.title"),
            dataIndex: "briefTitle",
            key: "briefTitle",
            fixed: isMobile ? false : "left",
            width: isMobile ? 240 : undefined,
            minWidth: isMobile ? 120 : 340,
            align: isMobile ? "center" : "left",
            render: (text: string) => {
                return (
                    <Typography.Text title={text} className="line-clamp-1 overflow-hidden font-ant-strong break-words">
                        {text}
                    </Typography.Text>
                );
            },
            onCell: data => {
                const { typeIndex = 0, typesCount = 1 } = data;
                return {
                    rowSpan: typeIndex === 0 ? typesCount : 0,
                };
            },
        },
        ...learners.map(learner => ({
            title: (
                <Typography.Text
                    title={`${learner.name || ""} ${learner.surname || ""}`.trim() ?? learner.email}
                    className="line-clamp-1 w-[80px] overflow-hidden text-center"
                >
                    {learner.name && learner.surname ? (
                        <span className="w-20 capitalize">
                            <span className="block truncate font-medium capitalize" title={learner.name}>
                                {learner.name.toLowerCase()}
                            </span>
                            <span className="block truncate capitalize" title={learner.surname}>
                                {learner.surname.toLowerCase()}
                            </span>
                        </span>
                    ) : (
                        <span className="line-clamp-1 overflow-hidden text-center break-all">{learner.email}</span>
                    )}
                </Typography.Text>
            ),
            dataIndex: learner["@id"] ?? "",
            key: learner["@id"] ?? "",
            width: 80,
            align: "center" as const,
            render: (status: BriefAssignmentState) =>
                status ? (
                    <span className="flex w-[80px] justify-center">
                        <BriefStatusBadge status={status} />
                    </span>
                ) : null,
        })),
    ];

    return columns;
}

/**
 * Generates table data (dataSource and columns) from the brief progress map
 */
const generateTableData = (
    classroom: IClassroom | undefined,
    data: IBriefAssignmentListQueryResult | undefined,
    t: TFunction,
    isMobile: boolean,
) => {
    const counts: EnumDictionary<BriefAssignmentState, number> = {
        [BriefAssignmentState.ASSIGNED]: 0,
        [BriefAssignmentState.RENDERED]: 0,
        [BriefAssignmentState.CORRECTED]: 0,
        [BriefAssignmentState.VALIDATED]: 0,
    };

    const learners = classroom?.learners ?? [];
    const assignments = data?.["hydra:member"] ?? [];

    // extract briefs and learners
    const usersMap = new Map<string, IBriefAssignmentListQueryResult["hydra:member"][number]["user"]>(
        learners.map(learner => [getResourceIRI(learner), learner]),
    );
    const briefsMap = new Map<string, IBriefAssignmentListQueryResult["hydra:member"][number]["brief"]>();
    const assignedBriefsMap = new Map<string, Set<number>>(); // briefId -> set of assignment indexes

    assignments.forEach((assignment, index) => {
        const user = assignment.user;
        const brief = assignment.brief;

        usersMap.set(getResourceIRI(user), user);
        briefsMap.set(getResourceIRI(brief), brief);
        assignedBriefsMap.set(
            getResourceIRI(brief),
            (assignedBriefsMap.get(getResourceIRI(brief)) ?? new Set<number>()).add(index),
        );

        // Count statuses
        const state = assignment.state;
        if (state in counts) {
            counts[state]++;
        }
    });

    const dataSource = data
        ? [
              ...briefsMap.entries().flatMap(([briefId, brief]) => {
                  // Get all assignments concerning this brief
                  const currentAssignments = [
                      ...(assignedBriefsMap.get(briefId)?.values() ?? []).map(index => assignments[index]),
                  ];

                  return entries(groupBy(currentAssignments, assignment => assignment.type)).map(
                      ([type, _assignments], index, array) => {
                          const row: Record<string, any> = {
                              key: `${briefId}_${type}`,
                              briefTitle: brief.title,
                              type,
                              typesCount: array.length,
                              typeIndex: index,
                          };
                          // For each learner, find their assignment for this brief
                          usersMap.forEach((user, userId) => {
                              const assignment = _assignments.find(
                                  a => getResourceIRI(a.brief) === briefId && getResourceIRI(a.user) === userId,
                              );

                              row[userId] = assignment ? assignment.state : "unassigned";
                          });

                          return row;
                      },
                  );
              }),
          ]
        : [];

    // Generate columns
    const columns = getColumns(classroom, t, isMobile);

    return { dataSource, columns, counts };
};

export const AssignmentsDashboard = ({ classroom, className }: { classroom?: IClassroom; className?: string }) => {
    const [t] = useTranslation(undefined, { keyPrefix: "features.dashboards.details.briefsProgress" });
    const screens = Grid.useBreakpoint();

    const { data, isLoading } = useBriefAssignmentListQuery({ filters: { classroom } });

    const isMobile = !screens.md;

    const {
        dataSource,
        columns,
        counts: statusCounts,
    } = useMemo(() => generateTableData(classroom, data, t, isMobile), [classroom, data, t, isMobile]);

    let children: ReactNode = null;

    if (isLoading) {
        children = <Skeleton.Node className="size-full!" active />;
    } else if (dataSource.length < 1 || columns.length < 2) {
        children = (
            <div className="flex size-full flex-col items-center justify-center">
                <Empty
                    description={
                        <div className="text-center text-color-ant-tertiary">{t("table.locale.emptyText")}</div>
                    }
                />
            </div>
        );
    } else {
        children = (
            <Table
                className="size-full rounded-none [&_.ant-table]:[--ant-table-header-border-radius:var(--ant-border-radius)]"
                tableLayout="auto"
                dataSource={dataSource}
                columns={columns}
                scroll={{ y: "100%" }}
                pagination={false}
                size="small"
                locale={{
                    emptyText: <Empty description={t(`table.locale.${isLoading ? "loadingText" : "emptyText"}`)} />,
                }}
                bordered
            />
        );
    }

    return (
        <div className={clsx("flex flex-col items-start gap-ant-md", className)}>
            <ProgressionLegend counts={statusCounts} />

            <div className="min-h-0 w-full flex-1 overflow-hidden">{children}</div>
        </div>
    );
};
